import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { getExport70New } from "../api/get-export-70-new";
import {
  CheckCircle2,
  ChevronDown,
  LayoutGrid,
  Loader2,
  TableProperties,
  XCircle,
  Calendar,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { putValidatorDetail } from "../api/put-validator-detail";
import { ProtectRole } from "@/components/ProtectRole";
import { ROLES } from "@/config/roles";

const formatRupiah = (angka: number | null | undefined) => {
  if (angka === null || angka === undefined) return "Rp 0";
  return new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    minimumFractionDigits: 0,
  }).format(angka);
};

export function RincianPerBulan({
  mappedMonths,
  showRupiah = false,
  showNilai = true,
  niu,
  year,
  semester,
  role,
  // totalPointKinerja = 0,
  pir = 4490,
  isAdmin = false,
  isAlreadyConfirmed = false,
  periodeId,
  isLolosSyaratMutlak = true,
}: {
  mappedMonths: any[];
  showRupiah?: boolean;
  showNilai?: boolean;
  niu: string;
  year: string | number;
  semester: string | number;
  isAdmin?: boolean;
  role?: string;
  totalPointKinerja?: number;
  pir?: number;
  isAlreadyConfirmed?: boolean;
  periodeId?: string | number;
  isLolosSyaratMutlak?: boolean;
}) {
  const [rincianViewMode, setRincianViewMode] = useState<"card" | "table">(
    "card",
  );

  const queryClient = useQueryClient();

  const { data: export70Data } = useQuery({
    queryKey: ["export-70-new", niu, year, semester, periodeId],
    queryFn: () =>
      getExport70New({
        niu,
        year,
        semester,
        periodeID: periodeId as string | number,
      }),
    enabled: !!niu && !!year && !!semester && !!periodeId,
  });

  const validatorMutation = useMutation({
    mutationFn: putValidatorDetail,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dosen-remun-detail", niu] });
      toast.success("Status validasi berhasil diperbarui.");
    },
    onError: (error: any) => {
      toast.error(error?.message || "Gagal memperbarui status validasi");
    },
  });

  const handleToggleValidasi = (month: string, is_validator: boolean) => {
    validatorMutation.mutate({
      year,
      semester,
      niu,
      month,
      is_validator,
      role: role || "",
    });
  };

  return (
    <>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <h2 className="text-xl font-bold tracking-tight text-slate-800">
            Rincian Per Bulan
          </h2>
          <Badge
            variant="secondary"
            className="rounded-full shadow-sm bg-slate-100 text-slate-700 border-none"
          >
            {mappedMonths.length} Bulan
          </Badge>
        </div>
        <div className="flex items-center bg-slate-100 p-1 rounded-lg">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setRincianViewMode("card")}
            className={`h-8 px-3 rounded-md transition-all ${rincianViewMode === "card" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
          >
            <LayoutGrid className="w-4 h-4 mr-2" />
            Grid
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setRincianViewMode("table")}
            className={`h-8 px-3 rounded-md transition-all ${rincianViewMode === "table" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
          >
            <TableProperties className="w-4 h-4 mr-2" />
            Tabel
          </Button>
        </div>
      </div>
      {rincianViewMode === "card" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 items-start">
          {mappedMonths.map((item, idx) => {
            // Format "YYYY_MM" -> "Bulan Tahun"
            const [y, m] = item.month.split("_");
            const monthName = new Date(
              parseInt(y),
              parseInt(m) - 1,
              1,
            ).toLocaleString("id-ID", { month: "long", year: "numeric" });

            // const rBersih = Number(item.rupiah_bersih_30) || null;
            // const rSerdos = Number(item.rupiah_serdos) || 0;
            // const total = rBersih || 0;

            const monthDataExport = export70Data?.find(
              (d: any) => d.Bulan === item.month,
            );
            const totalPointKinerja = monthDataExport
              ? Number(monthDataExport.PointPerformance)
              : 0;

            const isTugasBelajar =
              item.status_sekarang?.toLowerCase() === "tugas belajar";

            let isBelumMenerimaRemun = false;
            if (item.tgl_mulai_pembayaran_remun) {
              const tglMulai = new Date(item.tgl_mulai_pembayaran_remun);
              const tglMulaiValue =
                tglMulai.getFullYear() * 100 + (tglMulai.getMonth() + 1);
              const itemMonthValue = parseInt(y) * 100 + parseInt(m);
              if (tglMulaiValue > itemMonthValue) {
                isBelumMenerimaRemun = true;
              }
            }

            const effectivePointKinerja =
              isTugasBelajar || isBelumMenerimaRemun ? 0 : totalPointKinerja;

            const calculatedTotalPoin =
              isTugasBelajar || isLolosSyaratMutlak === false
                ? 0
                : Number(
                    (
                      item.nilai_struktural * (Number(item.cluster) / 100) +
                      effectivePointKinerja
                    ).toFixed(2),
                  );
            let multiplier = 1;
            let statusLabel = "";
            const isKontrak = item.status_pegawai?.toUpperCase() === "KONTRAK";
            const isPensiun = item.status_sekarang?.toLowerCase() === "pensiun";

            if (item.status_pegawai?.toUpperCase() === "PPPK") {
              multiplier = 0.6;
              statusLabel = "PPPK";
            } else if (item.status_pegawai?.toUpperCase() === "CPNS") {
              multiplier = 0.8;
              statusLabel = "CPNS";
            } else if (isKontrak) {
              multiplier = 0;
              statusLabel = "KONTRAK";
            } else if (isPensiun) {
              multiplier = 0;
              statusLabel = "PENSIUN";
            }

            const isNoRemun = isKontrak || isPensiun;

            const remunKotor100Base = isNoRemun ? 0 : calculatedTotalPoin * pir;
            const remunKotor100 = isNoRemun
              ? 0
              : remunKotor100Base * multiplier;

            const remunKotor30 = isNoRemun
              ? 0
              : item.rupiah_realisasi_30 !== null &&
                  item.rupiah_realisasi_30 !== undefined
                ? Number(item.rupiah_realisasi_30)
                : 0;

            const rupiahSerdos = isNoRemun
              ? 0
              : Number(item.rupiah_serdos || 0);
            const remunKotor70Base = isNoRemun
              ? 0
              : remunKotor100 - rupiahSerdos - remunKotor30;
            const remunKotor70 = isNoRemun ? 0 : remunKotor70Base;
            const pph100 = isNoRemun
              ? 0
              : remunKotor100 * Number(item.pph_21 || 0);
            const remunBersih70 = isNoRemun
              ? 0
              : remunKotor70 - remunKotor70 * Number(item.pph_21 || 0);

            const currentRoleKey =
              role?.replace(/\s+/g, "_").toLowerCase() || "";
            const currentRoleValidation = item.validator_details?.find(
              (v: any) =>
                v.level.replace(/\s+/g, "_").toLowerCase() === currentRoleKey,
            );
            const myValidationState = currentRoleValidation?.validator_name
              ? currentRoleValidation.is_valid_taken
                ? "valid"
                : "invalid"
              : "unvalidated";

            const isHtlValid =
              item.validator_details?.find((v: any) => v.level === "admin_htl")
                ?.is_valid_taken === true;
            const isKepegValid =
              item.validator_details?.find(
                (v: any) => v.level === "admin_kepegawaian",
              )?.is_valid_taken === true;
            const isDosenValidationDisabled =
              currentRoleKey === "dosen" && (!isHtlValid || !isKepegValid);
            let disabledReason: string | undefined = undefined;
            if (isDosenValidationDisabled) {
              if (!isHtlValid && !isKepegValid) {
                disabledReason = "Menunggu validasi Kepegawaian dan HTL";
              } else if (!isHtlValid) {
                disabledReason = "Menunggu validasi HTL";
              } else if (!isKepegValid) {
                disabledReason = "Menunggu validasi Kepegawaian";
              }
            }

            return (
              <Collapsible
                key={idx}
                defaultOpen={true || isAdmin}
                className="group/collapsible"
              >
                <Card
                  className={`shadow-sm border-slate-200 bg-white hover:border-primary/20 hover:shadow-md transition-all flex flex-col h-full ${
                    myValidationState === "valid"
                      ? "ring-2 ring-emerald-400 border-emerald-400"
                      : myValidationState === "invalid"
                        ? "ring-2 ring-red-400 border-red-400"
                        : ""
                  }`}
                >
                  <CardHeader className="bg-slate-50/50 border-b border-slate-100 pb-4">
                    <CollapsibleTrigger asChild>
                      <div className="flex flex-col gap-3 cursor-pointer hover:opacity-80 focus:outline-none w-full">
                        <div className="flex items-center justify-between space-y-0 w-full">
                          <div className="flex items-center gap-2">
                            <div className="h-6 w-6 rounded-full hover:bg-slate-200/50 flex items-center justify-center shrink-0">
                              <ChevronDown className="w-4 h-4 text-slate-500 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-180" />
                            </div>
                            <CardTitle className="text-base font-bold text-slate-700 transition-colors">
                              {monthName}
                            </CardTitle>
                          </div>
                          <div className="flex gap-1.5 shrink-0">
                            <Badge
                              variant="outline"
                              className="bg-purple-50 text-purple-700 border-purple-200 font-semibold px-2 py-0.5 text-[10px]"
                            >
                              {item.status_pegawai || "-"}
                            </Badge>
                            <Badge
                              variant="outline"
                              className={
                                item.status_sekarang === "Aktif"
                                  ? "bg-secondary text-primary border-primary/20 font-semibold px-2 py-0.5 text-[10px]"
                                  : "bg-amber-50 text-amber-700 border-amber-200 font-semibold px-2 py-0.5 text-[10px]"
                              }
                            >
                              {item.status_sekarang || "-"}
                            </Badge>
                          </div>
                        </div>

                        {isBelumMenerimaRemun && (
                          <div className="flex flex-col gap-1 text-[11px] sm:text-xs text-left whitespace-normal wrap-break-word w-full pr-4 pl-8">
                            <div className="text-rose-600 font-semibold">
                              Belum Berhak Menerima Remun
                            </div>
                            <div className="text-amber-600 flex items-start gap-1.5 font-medium">
                              <Calendar className="w-4 h-4 shrink-0 mt-0.5" />
                              <span>
                                Berhak menerima setelah 6 bulan pengaktifan
                                (mulai{" "}
                                {item.tgl_mulai_pembayaran_remun
                                  ? new Intl.DateTimeFormat("id-ID", {
                                      day: "numeric",
                                      month: "long",
                                      year: "numeric",
                                    }).format(
                                      new Date(item.tgl_mulai_pembayaran_remun),
                                    )
                                  : "-"}
                                )
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    </CollapsibleTrigger>

                    <CollapsibleContent>
                      <div className="mt-3 pt-3 border-t border-slate-200/60 text-left">
                        {/* List Validator Details */}
                        <div className="mb-3 flex flex-col gap-1.5 text-xs">
                          {["admin_kepegawaian", "admin_htl", "dosen"].map(
                            (level: string, vIdx: number) => {
                              const v = item.validator_details?.find(
                                (vd: any) => vd.level === level,
                              );
                              const isUnvalidated = !v || !v.validator_name;
                              const isValid = v && v.is_valid_taken === true;
                              const isInvalid =
                                v &&
                                !isUnvalidated &&
                                v.is_valid_taken === false;

                              const levelName =
                                level.toLowerCase() === "dosen"
                                  ? "DOSEN"
                                  : level.toLowerCase() === "admin super" ||
                                      level.toLowerCase() === "admin_super"
                                    ? "SUPER"
                                    : level
                                        .replace(/^admin[\s_]+/, "")
                                        .toUpperCase();
                              return (
                                <div
                                  key={vIdx}
                                  className="flex items-center justify-between bg-white px-2 py-1.5 rounded border border-slate-100 shadow-sm"
                                >
                                  <span className="font-medium text-slate-600">
                                    {levelName}
                                  </span>
                                  {isValid ? (
                                    <span className="flex items-center gap-1 text-[10px] font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-100">
                                      <CheckCircle2 className="w-3 h-3" />{" "}
                                      {/* {v.validator_name || "Valid"} */}
                                      {"Valid"}
                                    </span>
                                  ) : isInvalid ? (
                                    <span className="flex items-center gap-1 text-[10px] font-bold text-red-600 bg-red-50 px-1.5 py-0.5 rounded border border-red-100">
                                      <XCircle className="w-3 h-3" />{" "}
                                      {/* {v.validator_name || "Tidak Valid"} */}
                                      {"Tidak Valid"}
                                    </span>
                                  ) : (
                                    <span className="flex items-center gap-1 text-[10px] font-bold text-slate-400 bg-slate-50 px-1.5 py-0.5 rounded border border-slate-200">
                                      Belum Divalidasi
                                    </span>
                                  )}
                                </div>
                              );
                            },
                          )}
                        </div>

                        {!isAlreadyConfirmed && (
                          <ProtectRole
                            allowedRoles={[
                              ROLES.DOSEN,
                              ROLES.ADMIN_HTL,
                              ROLES.ADMIN_KEPEGAWAIAN,
                            ]}
                          >
                            {isDosenValidationDisabled ? (
                              <div className="flex items-center justify-center bg-slate-50 border border-slate-200 rounded-md h-9 shadow-sm">
                                <span className="text-[11px] text-slate-500 font-medium italic">
                                  {disabledReason}
                                </span>
                              </div>
                            ) : (
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className={`flex-1 text-xs h-9 font-semibold transition-all ${
                                    myValidationState === "invalid"
                                      ? "bg-red-500 text-white border-red-500 hover:bg-red-600 hover:text-white"
                                      : "text-red-600 border-red-200 bg-red-50 hover:bg-red-100 hover:text-red-700"
                                  }`}
                                  onClick={() =>
                                    handleToggleValidasi(item.month, false)
                                  }
                                  disabled={validatorMutation.isPending}
                                >
                                  {validatorMutation.isPending &&
                                  myValidationState === "invalid" ? (
                                    <Loader2 className="w-4 h-4 animate-spin mr-1.5" />
                                  ) : (
                                    <XCircle className="w-4 h-4 mr-1.5" />
                                  )}
                                  Tidak Valid
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className={`flex-1 text-xs h-9 font-semibold transition-all ${
                                    myValidationState === "valid"
                                      ? "bg-emerald-500 text-white border-emerald-500 hover:bg-emerald-600 hover:text-white"
                                      : "text-emerald-600 border-emerald-200 bg-emerald-50 hover:bg-emerald-100 hover:text-emerald-700"
                                  }`}
                                  onClick={() =>
                                    handleToggleValidasi(item.month, true)
                                  }
                                  disabled={validatorMutation.isPending}
                                >
                                  {validatorMutation.isPending &&
                                  myValidationState === "valid" ? (
                                    <Loader2 className="w-4 h-4 animate-spin mr-1.5" />
                                  ) : myValidationState === "unvalidated" ? (
                                    <span className="relative flex h-2 w-2 mr-1.5">
                                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                                      <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                                    </span>
                                  ) : (
                                    <CheckCircle2 className="w-4 h-4 mr-1.5" />
                                  )}
                                  Valid
                                </Button>
                              </div>
                            )}
                          </ProtectRole>
                        )}
                      </div>
                    </CollapsibleContent>
                  </CardHeader>

                  <CollapsibleContent className="flex flex-col grow">
                    <CardContent className="pt-5 space-y-5 grow">
                      {/* Jabatan Info */}
                      <div className="space-y-4">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-1.5">
                              Fungsional
                            </p>
                            <p className="text-sm font-medium text-slate-800">
                              {item.jabatan_fungsional || "-"}
                            </p>
                            {showNilai && (
                              <div className="flex items-center gap-3 mt-1.5 text-xs text-slate-500">
                                <span className="flex items-center gap-1 bg-slate-100 px-1.5 py-0.5 rounded">
                                  <span className="font-medium text-slate-700">
                                    Grade:
                                  </span>{" "}
                                  {item.grade_fungsional}
                                </span>
                                {/* <span className="flex items-center gap-1 bg-slate-100 px-1.5 py-0.5 rounded">
                                  <span className="font-medium text-slate-700">
                                    Poin:
                                  </span>{" "}
                                  {item.nilai_fungsional}
                                </span> */}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="flex items-start justify-between gap-4">
                          <div className="w-full">
                            <div className="flex justify-between items-center mb-1.5">
                              <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest">
                                Struktural
                              </p>
                              {(item as any).is_jabatan_double && (
                                <Badge
                                  variant="outline"
                                  className="text-[9px] bg-amber-50 text-amber-600 border-amber-200 uppercase tracking-wider py-0 px-1.5 shadow-none"
                                >
                                  Rangkap
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm font-medium text-slate-800">
                              {item.jabatan_struktural || "-"}
                            </p>
                            <div className="mt-2">
                              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest mb-0.5">
                                Golongan
                              </p>
                              <p className="text-xs font-medium text-slate-700">
                                {item.golongan || "-"}
                              </p>
                            </div>
                            {showNilai && (
                              <div className="flex items-center gap-2 mt-1.5 text-xs text-slate-500 flex-wrap">
                                <span className="flex items-center gap-1 bg-slate-100 px-1.5 py-0.5 rounded shrink-0">
                                  <span className="font-medium text-slate-700">
                                    Grade:
                                  </span>{" "}
                                  {item.grade_struktural}
                                </span>
                                <span className="flex items-center gap-1 bg-slate-100 px-1.5 py-0.5 rounded shrink-0">
                                  <span className="font-medium text-slate-700">
                                    Poin:
                                  </span>{" "}
                                  {item.nilai_struktural}
                                </span>
                                <span className="flex items-center gap-1 bg-slate-100 px-1.5 py-0.5 rounded shrink-0">
                                  <span className="font-medium text-slate-700">
                                    Poin (x Cluster):
                                  </span>{" "}
                                  {Number(
                                    (
                                      item.nilai_struktural *
                                      (Number(item.cluster) / 100)
                                    ).toFixed(2),
                                  )}
                                </span>
                              </div>
                            )}

                            {(item as any).is_jabatan_double &&
                              (item as any).jabatan_aktif &&
                              (item as any).jabatan_aktif.length > 0 && (
                                <div className="mt-3 pt-3 border-t border-slate-100">
                                  <Collapsible>
                                    <CollapsibleTrigger className="flex items-center justify-between w-full group">
                                      <p className="text-[10px] font-semibold text-amber-600/90 uppercase tracking-widest flex items-center gap-1.5">
                                        Rincian Jabatan Aktif
                                      </p>
                                      <ChevronDown className="w-4 h-4 text-amber-600/70 transition-transform duration-200 group-data-[state=open]:rotate-180" />
                                    </CollapsibleTrigger>
                                    <CollapsibleContent className="space-y-2 mt-2">
                                      {(item as any).jabatan_aktif.map(
                                        (jab: any, jIdx: number) => (
                                          <div
                                            key={jIdx}
                                            className="bg-amber-50/40 rounded-md p-2 border border-amber-100/50"
                                          >
                                            <p className="text-xs font-semibold text-slate-700">
                                              {jab.jbt_nama}
                                            </p>
                                            <p className="text-[10px] text-slate-500 mt-0.5">
                                              {jab.uk_nama}
                                            </p>
                                            <div className="flex items-center gap-2 mt-1.5 text-[10px]">
                                              {showNilai && (
                                                <span className="bg-white px-1.5 py-0.5 rounded border border-amber-200/60 text-amber-700 font-medium leading-none shadow-sm">
                                                  Grade: {jab.grade}
                                                </span>
                                              )}
                                              <span className="text-slate-400">
                                                {new Date(
                                                  jab.tgl_mulai,
                                                ).toLocaleDateString("id-ID", {
                                                  day: "numeric",
                                                  month: "short",
                                                  year: "numeric",
                                                })}
                                                {" - "}
                                                {jab.tgl_selesai
                                                  ? new Date(
                                                      jab.tgl_selesai,
                                                    ).toLocaleDateString(
                                                      "id-ID",
                                                      {
                                                        day: "numeric",
                                                        month: "short",
                                                        year: "numeric",
                                                      },
                                                    )
                                                  : "Sekarang"}
                                              </span>
                                            </div>
                                          </div>
                                        ),
                                      )}
                                    </CollapsibleContent>
                                  </Collapsible>
                                </div>
                              )}
                          </div>
                        </div>
                      </div>

                      <ProtectRole
                        allowedRoles={[
                          ROLES.ADMIN_SUPER,
                          ROLES.ADMIN_UNIV,
                          ...(showNilai ? [ROLES.DOSEN] : []),
                        ]}
                      >
                        {/* Nominal Info */}
                        <div className="bg-slate-50 rounded-lg p-3.5 space-y-2 border border-slate-100/80">
                          {showNilai && (
                            <>
                              <div className="flex justify-between items-center text-sm">
                                <span className="text-slate-500 font-medium">
                                  Poin Struktural
                                </span>
                                <span className="font-bold text-slate-800 tabular-nums">
                                  {Number(
                                    (
                                      (item.nilai_struktural || 0) *
                                      (Number(item.cluster || 0) / 100)
                                    ).toFixed(2),
                                  )}
                                </span>
                              </div>
                              <div className="flex justify-between items-center text-sm">
                                <span className="text-slate-500 font-medium flex flex-col">
                                  <span>Poin Performance</span>
                                  {isTugasBelajar && (
                                    <span className="text-[10px] text-rose-500 font-normal italic leading-tight mt-0.5">
                                      *Tidak diakui (Tugas Belajar)
                                    </span>
                                  )}
                                </span>
                                <span className="font-bold text-slate-800 tabular-nums">
                                  {effectivePointKinerja.toFixed(2)}
                                </span>
                              </div>
                              <div className="flex justify-between items-center text-sm">
                                <span className="text-slate-500 font-medium">
                                  Total Poin
                                </span>
                                <span className="font-bold text-slate-800 tabular-nums">
                                  {calculatedTotalPoin}
                                </span>
                              </div>
                              {statusLabel && (
                                <div className="flex justify-between items-center text-sm">
                                  <span className="text-slate-500 font-medium text-xs">
                                    Remun Kotor 100% (Dasar)
                                  </span>
                                  <span className="font-bold text-slate-700 tabular-nums text-xs">
                                    {showRupiah
                                      ? formatRupiah(remunKotor100Base)
                                      : "Rp ***"}
                                  </span>
                                </div>
                              )}
                              {statusLabel && (
                                <div className="flex justify-between items-center text-sm">
                                  <span className="text-slate-500 font-medium text-xs">
                                    Status ({statusLabel})
                                  </span>
                                  <span className="font-bold text-slate-800 tabular-nums text-xs">
                                    x {multiplier}
                                  </span>
                                </div>
                              )}
                              <div className="flex justify-between items-center text-sm">
                                <span className="text-slate-500 font-medium">
                                  Remun Kotor 100%
                                </span>
                                <span className="font-bold text-emerald-600 tabular-nums">
                                  {showRupiah
                                    ? formatRupiah(remunKotor100)
                                    : "Rp ***"}
                                </span>
                              </div>
                              <div className="flex justify-between items-center text-sm">
                                <span className="text-slate-500 font-medium">
                                  Rupiah Serdos
                                </span>
                                <span className="font-bold text-red-500 tabular-nums">
                                  {showRupiah
                                    ? formatRupiah(rupiahSerdos || 0)
                                    : "Rp ***"}
                                </span>
                              </div>
                            </>
                          )}
                          <div className="flex justify-between items-center text-sm">
                            <span className="text-slate-500 font-medium">
                              PPH
                            </span>
                            <div className="flex flex-col items-end">
                              <span className="font-semibold text-slate-700 tabular-nums">
                                {item.golongan || "-"} (
                                {Number(item.pph_21 || 0) * 100}%)
                              </span>
                              <span className="text-xs text-red-500 font-medium tabular-nums">
                                {showRupiah ? formatRupiah(pph100) : "Rp ***"}
                              </span>
                            </div>
                          </div>
                          {/* <div className="flex justify-between items-center text-sm">
                      <span className="text-slate-500 font-medium">Serdos</span>
                      <span className="font-bold text-slate-800 tabular-nums">
                        {showRupiah ? formatRupiah(rSerdos) : "Rp ***"}
                      </span>
                    </div> */}
                          <div className="flex flex-col gap-1 text-sm bg-secondary/10 p-2 rounded-md border border-secondary/20">
                            <span className="text-slate-600 font-bold border-b border-secondary/20 pb-1 mb-1">
                              Remun 30%
                            </span>
                            {/* <div className="flex justify-between items-center">
                        <span className="text-slate-500 font-medium text-xs">
                          Kotor
                        </span>
                        <span className="font-semibold text-amber-600 tabular-nums">
                          {item.rupiah_30_kotor !== null &&
                          item.rupiah_30_kotor !== undefined
                            ? showRupiah
                              ? formatRupiah(item.rupiah_30_kotor)
                              : "Rp ***"
                            : "-"}
                        </span>
                      </div> */}
                            <div className="flex justify-between items-center">
                              <span className="text-slate-500 font-medium text-xs">
                                Realisasi Kotor
                              </span>
                              <span className="font-semibold text-indigo-600 tabular-nums">
                                {item.rupiah_realisasi_30 !== null &&
                                item.rupiah_realisasi_30 !== undefined
                                  ? showRupiah
                                    ? formatRupiah(item.rupiah_realisasi_30)
                                    : "Rp ***"
                                  : "-"}
                              </span>
                            </div>
                            {/* <div className="flex justify-between items-center">
                        <span className="text-primary font-semibold text-xs">
                          Bersih
                        </span>
                        <span className="font-bold text-primary tabular-nums">
                          {rBersih !== null
                            ? showRupiah
                              ? formatRupiah(rBersih)
                              : "Rp ***"
                            : "-"}
                        </span>
                      </div> */}
                          </div>
                          <div className="flex flex-col gap-1 text-sm bg-secondary/10 p-2 rounded-md border border-secondary/20 mt-2">
                            <span className="text-slate-600 font-bold border-b border-secondary/20 pb-1 mb-1">
                              Remun 70%
                            </span>
                            <div className="flex justify-between items-center">
                              <span className="text-slate-500 font-medium text-xs">
                                Kotor
                              </span>
                              <div className="flex flex-col items-end">
                                <span className="font-semibold text-blue-600 tabular-nums">
                                  {showRupiah
                                    ? formatRupiah(Math.max(0, remunKotor70))
                                    : "Rp ***"}
                                </span>
                              </div>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-primary font-semibold text-xs">
                                Bersih
                              </span>
                              <span className="font-bold text-primary tabular-nums">
                                {showRupiah
                                  ? formatRupiah(Math.max(0, remunBersih70))
                                  : "Rp ***"}
                              </span>
                            </div>
                          </div>
                        </div>
                      </ProtectRole>
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            );
          })}
        </div>
      ) : (
        <Card className="shadow-sm border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader className="bg-slate-50/80">
                <TableRow className="hover:bg-transparent h-10">
                  <TableHead className="w-24 text-slate-600 font-bold text-[11px] uppercase tracking-wider">
                    Bulan
                  </TableHead>
                  <TableHead className="w-20 text-center text-slate-600 font-bold min-w-40 text-[11px] uppercase tracking-wider">
                    Validasi
                  </TableHead>
                  <TableHead className="min-w-40 w-40 max-w-40 text-slate-600 font-bold text-[11px] uppercase tracking-wider whitespace-normal wrap-break-word leading-tight">
                    Status & Jabatan Fungsional
                  </TableHead>
                  <TableHead className="min-w-48 w-48 max-w-48 text-slate-600 font-bold text-[11px] uppercase tracking-wider whitespace-normal wrap-break-word leading-tight">
                    Jabatan Struktural
                  </TableHead>
                  {showNilai && (
                    <>
                      <TableHead className="text-center text-slate-600 font-bold text-[11px] uppercase tracking-wider leading-tight">
                        Poin Performance
                      </TableHead>
                      <TableHead className="text-center text-slate-600 font-bold text-[11px] uppercase tracking-wider leading-tight">
                        Total Poin
                      </TableHead>
                    </>
                  )}
                  <ProtectRole
                    allowedRoles={[
                      ROLES.ADMIN_SUPER,
                      ROLES.ADMIN_UNIV,
                      // ROLES.DOSEN,
                    ]}
                  >
                    <TableHead className="text-center text-slate-600 font-bold text-[11px] uppercase tracking-wider leading-tight">
                      {showNilai ? "Remun 100% & PPH" : "PPH"}
                    </TableHead>
                    {/* <TableHead className="text-center text-slate-600 font-bold text-[11px] uppercase tracking-wider leading-tight">
                    Serdos
                  </TableHead> */}
                    <TableHead className="text-center text-slate-600 font-bold text-[11px] uppercase tracking-wider leading-tight">
                      Remun 30%
                    </TableHead>
                    <TableHead className="text-center text-slate-600 font-bold text-[11px] uppercase tracking-wider leading-tight">
                      Remun 70%
                    </TableHead>
                  </ProtectRole>
                  {!isAlreadyConfirmed && (
                    <ProtectRole
                      allowedRoles={[
                        ROLES.DOSEN,
                        ROLES.ADMIN_HTL,
                        ROLES.ADMIN_KEPEGAWAIAN,
                      ]}
                    >
                      <TableHead className="w-45 text-center text-slate-600 font-bold text-[11px] uppercase tracking-wider">
                        Aksi
                      </TableHead>
                    </ProtectRole>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {mappedMonths.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={12}
                      className="text-center py-8 text-slate-500"
                    >
                      Data rincian per bulan tidak tersedia
                    </TableCell>
                  </TableRow>
                ) : (
                  mappedMonths.map((item, idx) => {
                    const [y, m] = item.month.split("_");
                    const monthName = new Date(
                      parseInt(y),
                      parseInt(m) - 1,
                      1,
                    ).toLocaleString("id-ID", {
                      month: "short",
                    });
                    // const rBersih = Number(item.rupiah_bersih_30) || null;
                    // const rSerdos = Number(item.rupiah_serdos) || 0;
                    // const total = rBersih || 0;

                    const monthDataExport = export70Data?.find(
                      (d: any) => d.Bulan === item.month,
                    );
                    const totalPointKinerja = monthDataExport
                      ? Number(monthDataExport.PointPerformance)
                      : 0;

                    const isTugasBelajar =
                      item.status_sekarang?.toLowerCase() === "tugas belajar";

                    let isBelumMenerimaRemun = false;
                    if (item.tgl_mulai_pembayaran_remun) {
                      const tglMulai = new Date(
                        item.tgl_mulai_pembayaran_remun,
                      );
                      const tglMulaiValue =
                        tglMulai.getFullYear() * 100 +
                        (tglMulai.getMonth() + 1);
                      const itemMonthValue = parseInt(y) * 100 + parseInt(m);
                      if (tglMulaiValue > itemMonthValue) {
                        isBelumMenerimaRemun = true;
                      }
                    }

                    const effectivePointKinerja =
                      isTugasBelajar || isBelumMenerimaRemun
                        ? 0
                        : totalPointKinerja;

                    const calculatedTotalPoin =
                      isTugasBelajar || isLolosSyaratMutlak === false
                        ? 0
                        : Number(
                            (
                              item.nilai_struktural *
                                (Number(item.cluster) / 100) +
                              effectivePointKinerja
                            ).toFixed(2),
                          );
                    let multiplier = 1;
                    let statusLabel = "";
                    const isKontrak =
                      item.status_pegawai?.toUpperCase() === "KONTRAK";
                    const isPensiun =
                      item.status_sekarang?.toLowerCase() === "pensiun";

                    if (item.status_pegawai?.toUpperCase() === "PPPK") {
                      multiplier = 0.6;
                      statusLabel = "PPPK";
                    } else if (item.status_pegawai?.toUpperCase() === "CPNS") {
                      multiplier = 0.8;
                      statusLabel = "CPNS";
                    } else if (isKontrak) {
                      multiplier = 0;
                      statusLabel = "KONTRAK";
                    } else if (isPensiun) {
                      multiplier = 0;
                      statusLabel = "PENSIUN";
                    }

                    const isNoRemun = isKontrak || isPensiun;

                    const remunKotor100Base = isNoRemun
                      ? 0
                      : calculatedTotalPoin * pir;
                    const remunKotor100 = isNoRemun
                      ? 0
                      : remunKotor100Base * multiplier;

                    const remunKotor30 = isNoRemun
                      ? 0
                      : item.rupiah_realisasi_30 !== null &&
                          item.rupiah_realisasi_30 !== undefined
                        ? Number(item.rupiah_realisasi_30)
                        : 0;

                    const rupiahSerdos = isNoRemun
                      ? 0
                      : Number(item.rupiah_serdos || 0);
                    const remunKotor70Base = isNoRemun
                      ? 0
                      : remunKotor100 - rupiahSerdos - remunKotor30;
                    const remunKotor70 = isNoRemun ? 0 : remunKotor70Base;
                    const pph100 = isNoRemun
                      ? 0
                      : remunKotor100 * Number(item.pph_21 || 0);
                    const remunBersih70 = isNoRemun
                      ? 0
                      : remunKotor70 - remunKotor70 * Number(item.pph_21 || 0);

                    const currentRoleKey =
                      role?.replace(/\s+/g, "_").toLowerCase() || "";
                    const currentRoleValidation = item.validator_details?.find(
                      (v: any) =>
                        v.level.replace(/\s+/g, "_").toLowerCase() ===
                        currentRoleKey,
                    );
                    const myValidationState =
                      currentRoleValidation?.validator_name
                        ? currentRoleValidation.is_valid_taken
                          ? "valid"
                          : "invalid"
                        : "unvalidated";

                    const isHtlValid =
                      item.validator_details?.find(
                        (v: any) => v.level === "admin_htl",
                      )?.is_valid_taken === true;
                    const isKepegValid =
                      item.validator_details?.find(
                        (v: any) => v.level === "admin_kepegawaian",
                      )?.is_valid_taken === true;
                    const isDosenValidationDisabled =
                      currentRoleKey === "dosen" &&
                      (!isHtlValid || !isKepegValid);
                    const disabledReason = isDosenValidationDisabled
                      ? "Harus divalidasi oleh Admin HTL dan Kepegawaian terlebih dahulu"
                      : undefined;
                    console.log({ disabledReason });

                    return (
                      <TableRow
                        key={idx}
                        className={`hover:bg-slate-50 transition-colors ${
                          myValidationState === "valid"
                            ? "bg-emerald-50/30"
                            : myValidationState === "invalid"
                              ? "bg-red-50/30"
                              : ""
                        }`}
                      >
                        <TableCell className="font-semibold text-slate-700 whitespace-nowrap">
                          {monthName}
                        </TableCell>
                        <TableCell className="text-center">
                          {/* List Validator Details */}
                          <div className="mb-2 flex flex-col gap-1 text-left text-[10px]">
                            {["admin_kepegawaian", "admin_htl", "dosen"].map(
                              (level: string, vIdx: number) => {
                                const v = item.validator_details?.find(
                                  (vd: any) => vd.level === level,
                                );
                                const isUnvalidated = !v || !v.validator_name;
                                const isValid = v && v.is_valid_taken === true;
                                const isInvalid =
                                  v &&
                                  !isUnvalidated &&
                                  v.is_valid_taken === false;

                                const levelName =
                                  level.toLowerCase() === "dosen"
                                    ? "DOSEN"
                                    : level.toLowerCase() === "admin super" ||
                                        level.toLowerCase() === "admin_super"
                                      ? "SUPER"
                                      : level
                                          .replace(/^admin[\s_]+/, "")
                                          .toUpperCase();
                                return (
                                  <div
                                    key={vIdx}
                                    className="flex items-center justify-between bg-white px-1.5 py-1 rounded border border-slate-100 shadow-sm gap-2"
                                  >
                                    <span className="font-medium text-slate-600">
                                      {levelName}
                                    </span>
                                    {isValid ? (
                                      <span className="flex items-center gap-1 text-[9px] font-bold text-emerald-600 bg-emerald-50 px-1 py-0.5 rounded border border-emerald-100 whitespace-nowrap">
                                        <CheckCircle2 className="w-2.5 h-2.5" />{" "}
                                        {/* {v.validator_name || "Valid"} */}
                                        {"Valid"}
                                      </span>
                                    ) : isInvalid ? (
                                      <span className="flex items-center gap-1 text-[9px] font-bold text-red-600 bg-red-50 px-1 py-0.5 rounded border border-red-100 whitespace-nowrap">
                                        <XCircle className="w-2.5 h-2.5" />{" "}
                                        {/* {v.validator_name || "Tidak Valid"} */}
                                        {"Tidak Valid"}
                                      </span>
                                    ) : (
                                      <span className="flex items-center gap-1 text-[9px] font-bold text-slate-400 bg-slate-50 px-1 py-0.5 rounded border border-slate-200">
                                        Belum Divalidasi
                                      </span>
                                    )}
                                  </div>
                                );
                              },
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="min-w-40 w-40 max-w-40 whitespace-normal wrap-break-word align-top">
                          <div className="flex flex-col gap-1 mb-1.5 items-start">
                            <div className="flex flex-wrap gap-1">
                              <Badge
                                variant="outline"
                                className="bg-purple-50 text-purple-700 border-purple-200 font-semibold px-1.5 py-0 text-[9px]"
                              >
                                {item.status_pegawai || "-"}
                              </Badge>
                              <Badge
                                variant="outline"
                                className={
                                  item.status_sekarang === "Aktif"
                                    ? "bg-secondary text-primary border-primary/20 font-semibold px-1.5 py-0 text-[9px]"
                                    : "bg-amber-50 text-amber-700 border-amber-200 font-semibold px-1.5 py-0 text-[9px]"
                                }
                              >
                                {item.status_sekarang || "-"}
                              </Badge>
                            </div>

                            {isBelumMenerimaRemun && (
                              <div className="flex flex-col gap-1 text-[9px] text-left whitespace-normal wrap-break-word w-full mt-1">
                                <div className="text-rose-600 font-semibold leading-tight">
                                  Belum Berhak Menerima Remun
                                </div>
                                <div className="text-amber-600 flex items-start gap-1 font-medium leading-tight">
                                  <Calendar className="w-3 h-3 shrink-0" />
                                  <span>
                                    Berhak menerima setelah 6 bulan pengaktifan
                                    (mulai{" "}
                                    {item.tgl_mulai_pembayaran_remun
                                      ? new Intl.DateTimeFormat("id-ID", {
                                          day: "numeric",
                                          month: "short",
                                          year: "numeric",
                                        }).format(
                                          new Date(
                                            item.tgl_mulai_pembayaran_remun,
                                          ),
                                        )
                                      : "-"}
                                    )
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                          <p className="text-[11px] font-semibold text-slate-800 leading-snug">
                            {item.jabatan_fungsional || "-"}
                          </p>
                          {showNilai && (
                            <div className="flex flex-col items-start gap-1 mt-1.5 text-[10px] text-slate-500">
                              <span className="flex items-center gap-1 bg-slate-100 px-1.5 py-0.5 rounded">
                                <span className="font-medium text-slate-700">
                                  Grade:
                                </span>{" "}
                                {item.grade_fungsional}
                              </span>
                              {/* <span className="flex items-center gap-1 bg-slate-100 px-1.5 py-0.5 rounded">
                                <span className="font-medium text-slate-700">
                                  Poin:
                                </span>{" "}
                                {item.nilai_fungsional}
                              </span> */}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="min-w-48 w-48 max-w-48 whitespace-normal wrap-break-word align-top">
                          <div className="flex items-start gap-1.5 mb-1 flex-wrap">
                            <p
                              className="text-[11px] font-semibold text-slate-800 leading-snug wrap-break-word"
                              title={item.jabatan_struktural || "-"}
                            >
                              {item.jabatan_struktural || "-"}
                            </p>
                            {(item as any).is_jabatan_double && (
                              <Badge
                                variant="outline"
                                className="text-[9px] bg-amber-50 text-amber-600 border-amber-200 uppercase tracking-wider py-0 px-1.5 shadow-none shrink-0"
                              >
                                Rangkap
                              </Badge>
                            )}
                          </div>
                          <div className="mt-1.5 mb-1">
                            <p className="text-[9px] font-semibold text-slate-400 uppercase tracking-widest mb-0.5">
                              Golongan
                            </p>
                            <p className="text-[10px] font-medium text-slate-700">
                              {item.golongan || "-"}
                            </p>
                          </div>
                          {showNilai && (
                            <div className="flex flex-col items-start gap-1 mt-1.5 text-[10px] text-slate-500">
                              <span className="flex items-center gap-1 bg-slate-100 px-1.5 py-0.5 rounded shrink-0">
                                <span className="font-medium text-slate-700">
                                  Grade:
                                </span>{" "}
                                {item.grade_struktural}
                              </span>
                              <span className="flex items-center gap-1 bg-slate-100 px-1.5 py-0.5 rounded shrink-0">
                                <span className="font-medium text-slate-700">
                                  Poin:
                                </span>{" "}
                                {item.nilai_struktural}
                              </span>
                              <span className="flex items-center gap-1 bg-slate-100 px-1.5 py-0.5 rounded shrink-0">
                                <span className="font-medium text-slate-700">
                                  Poin (x Cluster):
                                </span>{" "}
                                {Number(
                                  (
                                    item.nilai_struktural *
                                    (Number(item.cluster) / 100)
                                  ).toFixed(2),
                                )}
                              </span>
                            </div>
                          )}

                          {(item as any).is_jabatan_double &&
                            (item as any).jabatan_aktif &&
                            (item as any).jabatan_aktif.length > 0 && (
                              <div className="mt-3 pt-3 border-t border-slate-100">
                                <Collapsible>
                                  <CollapsibleTrigger className="flex items-center justify-between w-full group">
                                    <p className="text-[10px] font-semibold text-amber-600/90 uppercase tracking-widest flex items-center gap-1.5">
                                      Rincian Jabatan Aktif
                                    </p>
                                    <ChevronDown className="w-4 h-4 text-amber-600/70 transition-transform duration-200 group-data-[state=open]:rotate-180" />
                                  </CollapsibleTrigger>
                                  <CollapsibleContent className="space-y-2 mt-2">
                                    {(item as any).jabatan_aktif.map(
                                      (jab: any, jIdx: number) => (
                                        <div
                                          key={jIdx}
                                          className="bg-amber-50/40 rounded-md p-2 border border-amber-100/50"
                                        >
                                          <p className="text-xs font-semibold text-slate-700">
                                            {jab.jbt_nama}
                                          </p>
                                          <p className="text-[10px] text-slate-500 mt-0.5">
                                            {jab.uk_nama}
                                          </p>
                                          <div className="flex items-center gap-2 mt-1.5 text-[10px]">
                                            {showNilai && (
                                              <span className="bg-white px-1.5 py-0.5 rounded border border-amber-200/60 text-amber-700 font-medium leading-none shadow-sm">
                                                Grade: {jab.grade}
                                              </span>
                                            )}
                                            <span className="text-slate-400">
                                              {new Date(
                                                jab.tgl_mulai,
                                              ).toLocaleDateString("id-ID", {
                                                day: "numeric",
                                                month: "short",
                                                year: "numeric",
                                              })}
                                              {" - "}
                                              {jab.tgl_selesai
                                                ? new Date(
                                                    jab.tgl_selesai,
                                                  ).toLocaleDateString(
                                                    "id-ID",
                                                    {
                                                      day: "numeric",
                                                      month: "short",
                                                      year: "numeric",
                                                    },
                                                  )
                                                : "Sekarang"}
                                            </span>
                                          </div>
                                        </div>
                                      ),
                                    )}
                                  </CollapsibleContent>
                                </Collapsible>
                              </div>
                            )}
                        </TableCell>
                        <ProtectRole
                          allowedRoles={[
                            ROLES.ADMIN_SUPER,
                            ROLES.ADMIN_UNIV,
                            ...(showNilai ? [ROLES.DOSEN] : []),
                          ]}
                        >
                          {showNilai && (
                            <>
                              <TableCell className="text-center font-semibold text-slate-700 tabular-nums">
                                <div>{effectivePointKinerja.toFixed(2)}</div>
                                {isTugasBelajar && (
                                  <div className="text-[9px] text-rose-500 font-normal italic mt-1 leading-tight">
                                    *Tidak diakui
                                    <br />
                                    (Tugas Belajar)
                                  </div>
                                )}
                              </TableCell>
                              <TableCell className="text-center font-bold text-slate-800 tabular-nums">
                                {calculatedTotalPoin}
                              </TableCell>
                            </>
                          )}

                          <TableCell className="text-center whitespace-nowrap align-middle">
                            <div className="flex flex-col gap-1.5 items-center justify-center">
                              {showNilai && (
                                <div className="flex flex-col items-center gap-0.5">
                                  {statusLabel && (
                                    <>
                                      <span className="text-[10px] text-slate-500 font-medium">
                                        Kotor 100% (Dasar):
                                      </span>
                                      <span className="font-semibold text-slate-700 tabular-nums text-[11px]">
                                        {showRupiah
                                          ? formatRupiah(remunKotor100Base)
                                          : "Rp ***"}
                                      </span>
                                      <span className="text-[10px] text-slate-500 font-medium mt-1">
                                        Status ({statusLabel}):
                                      </span>
                                      <span className="font-semibold text-slate-800 tabular-nums text-[11px]">
                                        x {multiplier}
                                      </span>
                                    </>
                                  )}
                                  <span
                                    className={`text-[10px] text-slate-500 font-medium ${statusLabel ? "mt-1" : ""}`}
                                  >
                                    Kotor 100%:
                                  </span>
                                  <span className="font-semibold text-emerald-600 tabular-nums">
                                    {showRupiah
                                      ? formatRupiah(remunKotor100)
                                      : "Rp ***"}
                                  </span>
                                </div>
                              )}
                              <div className="flex flex-col items-center gap-0.5">
                                <span className="text-[10px] text-slate-500 font-medium">
                                  PPH {item.golongan || "-"} (
                                  {Number(item.pph_21 || 0) * 100}%)
                                </span>
                                <span className="text-xs text-red-500 font-medium tabular-nums">
                                  {showRupiah ? formatRupiah(pph100) : "Rp ***"}
                                </span>
                              </div>
                            </div>
                          </TableCell>
                          {/* <TableCell className="text-center tabular-nums text-slate-600 whitespace-nowrap">
                          {showRupiah ? formatRupiah(rSerdos) : "Rp ***"}
                        </TableCell> */}
                          <TableCell className="text-center whitespace-nowrap">
                            <div className="flex flex-col gap-1 items-center">
                              <div className="flex items-center gap-2 text-xs">
                                <span className="text-slate-500 font-medium">
                                  Realisasi Kotor:
                                </span>
                                <span className="font-semibold text-indigo-600 tabular-nums">
                                  {item.rupiah_realisasi_30 !== null &&
                                  item.rupiah_realisasi_30 !== undefined
                                    ? showRupiah
                                      ? formatRupiah(item.rupiah_realisasi_30)
                                      : "Rp ***"
                                    : "-"}
                                </span>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="text-center whitespace-nowrap">
                            <div className="flex flex-col gap-1 items-center">
                              <div className="flex flex-col items-center gap-0.5 text-xs">
                                <div className="flex items-center gap-2">
                                  <span className="text-slate-500 font-medium">
                                    Kotor:
                                  </span>
                                  <span className="font-semibold text-blue-600 tabular-nums">
                                    {showRupiah
                                      ? formatRupiah(Math.max(0, remunKotor70))
                                      : "Rp ***"}
                                  </span>
                                </div>
                                {multiplier !== 1 && (
                                  <span className="text-[9px] text-slate-400 tabular-nums leading-none">
                                    (
                                    {showRupiah
                                      ? formatRupiah(
                                          Math.max(0, remunKotor70Base),
                                        )
                                      : "Rp ***"}{" "}
                                    x {multiplier} {statusLabel})
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-2 text-xs bg-secondary/20 px-2 py-0.5 rounded">
                                <span className="text-primary font-medium">
                                  Bersih:
                                </span>
                                <span className="font-bold text-primary tabular-nums">
                                  {showRupiah
                                    ? formatRupiah(Math.max(0, remunBersih70))
                                    : "Rp ***"}
                                </span>
                              </div>
                            </div>
                          </TableCell>
                        </ProtectRole>
                        {!isAlreadyConfirmed && (
                          <ProtectRole
                            allowedRoles={[
                              ROLES.DOSEN,
                              ROLES.ADMIN_HTL,
                              ROLES.ADMIN_KEPEGAWAIAN,
                            ]}
                          >
                            <TableCell className="align-middle">
                              <div className="flex flex-col gap-1 items-center">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className={`w-full text-[10px] h-7 font-semibold transition-all ${
                                    myValidationState === "valid"
                                      ? "bg-emerald-500 text-white border-emerald-500 hover:bg-emerald-600 hover:text-white"
                                      : "text-emerald-600 border-emerald-200 bg-emerald-50 hover:bg-emerald-100 hover:text-emerald-700"
                                  }`}
                                  onClick={() =>
                                    handleToggleValidasi(item.month, true)
                                  }
                                  disabled={validatorMutation.isPending}
                                >
                                  {validatorMutation.isPending &&
                                  myValidationState === "valid" ? (
                                    <Loader2 className="w-3 h-3 animate-spin mr-1" />
                                  ) : myValidationState === "unvalidated" ? (
                                    <span className="relative flex h-1.5 w-1.5 mr-1">
                                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                                      <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500"></span>
                                    </span>
                                  ) : (
                                    <CheckCircle2 className="w-3 h-3 mr-1" />
                                  )}
                                  Valid
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className={`w-full text-[10px] h-7 font-semibold transition-all ${
                                    myValidationState === "invalid"
                                      ? "bg-red-500 text-white border-red-500 hover:bg-red-600 hover:text-white"
                                      : "text-red-600 border-red-200 bg-red-50 hover:bg-red-100 hover:text-red-700"
                                  }`}
                                  onClick={() =>
                                    handleToggleValidasi(item.month, false)
                                  }
                                  disabled={validatorMutation.isPending}
                                >
                                  {validatorMutation.isPending &&
                                  myValidationState === "invalid" ? (
                                    <Loader2 className="w-3 h-3 animate-spin mr-1" />
                                  ) : (
                                    <XCircle className="w-3 h-3 mr-1" />
                                  )}
                                  Tidak Valid
                                </Button>
                              </div>
                            </TableCell>
                          </ProtectRole>
                        )}
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}
    </>
  );
}
